import { useMemo } from 'react';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { loginInfoApi } from '@/api/services/onboarding';
import type { LoginInfoDatatableFiltersDto, LoginInfoDto } from '@/api/types/onboarding';
import { fmtDate } from '@/features/tenant/swapFreeControl/helpers';
import { formatNumber } from '@/utils/format';
import { useFilePicker } from '../useFilePicker';

const P = 'User.Onboarding.LoginInformation';

export function LoginInformationTab() {
  const toast = useToast();
  const can = usePermission();
  const canDownloadExcel = can(`${P}.DownloadExcel`);
  const canDownloadTemplate = can(`${P}.DownloadTemplate`);
  const canUpload = can(`${P}.UploadExcel`);

  const table = useServerDataTable<LoginInfoDto, Partial<LoginInfoDatatableFiltersDto>>({
    queryKey: ['onboarding', 'login-info'],
    fetcher: (req) => loginInfoApi.getListForDataTable(req as LoginInfoDatatableFiltersDto),
    pageSize: 5,
  });

  const columns = useMemo<DataGridColumn<LoginInfoDto>[]>(
    () => [
      { key: 'login', title: 'Login', className: 'text-end', value: (r) => r.login },
      { key: 'uniqueId', title: 'Unique Identifier', className: 'text-start', value: (r) => r.uniqueId },
      { key: 'group', title: 'Group', className: 'text-start', filterable: false, value: (r) => r.group },
      { key: 'country', title: 'Country', className: 'text-start', filterable: false, value: (r) => r.country },
      { key: 'amlRisk', title: 'Aml Risk', className: 'text-start', filterable: false, value: (r) => r.amlRisk ?? '' },
      { key: 'netWorth', title: 'Net Worth', className: 'text-end', filterable: false, value: (r) => r.netWorth, cell: (r) => formatNumber(r.netWorth) },
      { key: 'annualIncome', title: 'Annual Income', className: 'text-end', filterable: false, value: (r) => r.annualIncome, cell: (r) => formatNumber(r.annualIncome) },
      { key: 'incomeSource', title: 'Income Source', className: 'text-start', filterable: false, value: (r) => r.incomeSource ?? '' },
      { key: 'mifidClassification', title: 'MiFId Classification', className: 'text-start', filterable: false, value: (r) => r.mifidClassification },
      { key: 'dataSource', title: 'Connector', className: 'text-start', filterable: false, value: (r) => r.dataSource },
      { key: 'birthdate', title: 'Birth Date', className: 'text-start', filterable: false, value: (r) => r.birthdate ?? '', cell: (r) => (r.birthdate ? fmtDate(r.birthdate) : '') },
      { key: 'riskOfLoss', title: 'Risk Of Loss', className: 'text-end', filterable: false, value: (r) => r.riskOfLoss, cell: (r) => formatNumber(r.riskOfLoss) },
      { key: 'unstable', title: 'Unstable', className: 'text-center', filterable: false, value: (r) => r.unstable },
    ],
    [],
  );

  const picker = useFilePicker('.xlsx,.xls', async (file) => {
    try {
      await loginInfoApi.uploadExcel(file);
      toast.success('Upload completed.');
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed.');
    }
  });

  const toolbar = (
    <div className="d-flex gap-2">
      {canDownloadTemplate && (
        <AsyncButton
          className="btn btn-info btn-sm"
          icon="fa fa-file-excel"
          title="Download an empty Excel template"
          onClick={() =>
            loginInfoApi.downloadTemplate().catch((e) =>
              toast.error(e instanceof ApiError ? e.message : 'Download failed.'),
            )
          }
        >
          Download Template
        </AsyncButton>
      )}
      {canUpload && (
        <AsyncButton
          className="btn btn-primary btn-sm"
          icon="fa fa-upload"
          title="Upload login information from Excel"
          onClick={() => picker.open()}
        >
          Upload
        </AsyncButton>
      )}
    </div>
  );

  return (
    <div>
      {picker.input}
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Login Information"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />
    </div>
  );
}
